import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getSignedUrlsCached, extractFilePath } from '../lib/storage';
import { queryKeys } from '../lib/queryKeys';

export type StagingJobStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'canceled';

export interface StagingJobWithContext {
  id: string;
  status: StagingJobStatus;
  queued_at: string;
  started_at: string | null;
  completed_at: string | null;
  attempts: number;
  last_error: string | null;
  image_id: string;
  project_id: string;
  project_title: string;
  project_address: string | null;
  original_url: string;
  original_signed_url: string;
  staged_signed_url?: string | null;
  staged_url?: string | null;
  style_label: string | null;
  structure_type: string | null;
}

type StagedImageJoin = {
  staged_url?: string | null;
  style?: string | null;
  structure_type?: string | null;
} | null;

type JobWithJoins = {
  id: string;
  status: string;
  queued_at: string;
  started_at: string | null;
  completed_at: string | null;
  attempts: number;
  last_error: string | null;
  image_id: string;
  request_payload?: Record<string, unknown> | null;
  staged_images?: StagedImageJoin;
  images: {
    id: string;
    original_url: string;
    project_id: string;
    projects: {
      id: string;
      title: string;
      property_address: string | null;
      user_id: string;
    };
  };
};

async function fetchStagingJobs(userId: string): Promise<StagingJobWithContext[]> {
  const { data, error: fetchError } = await supabase
    .from('staging_jobs')
    .select(`
      id,
      status,
      queued_at,
      started_at,
      completed_at,
      attempts,
      last_error,
      image_id,
      request_payload,
      staged_images (
        id,
        staged_url,
        style,
        structure_type,
        created_at
      ),
      images!inner (
        id,
        original_url,
        project_id,
        projects!inner (
          id,
          title,
          property_address,
          user_id
        )
      )
    `)
    .eq('images.projects.user_id', userId)
    .in('status', ['queued', 'processing', 'completed'])
    .order('queued_at', { ascending: false });

  if (fetchError) throw fetchError;
  if (!data || data.length === 0) return [];

  const filePaths = new Set<string>();
  data.forEach((item) => {
    const imageData = (item as JobWithJoins).images;
    const stagedData = (item as JobWithJoins).staged_images;
    filePaths.add(extractFilePath(imageData.original_url));
    if (stagedData?.staged_url) {
      filePaths.add(extractFilePath(stagedData.staged_url));
    }
  });

  const signedUrlMap = await getSignedUrlsCached(Array.from(filePaths));

  return data.map((item) => {
    const imageData = (item as JobWithJoins).images;
    const payload = ((item as JobWithJoins).request_payload || {}) as Record<string, unknown>;
    const stagedData = (item as JobWithJoins).staged_images;
    const originalPath = extractFilePath(imageData.original_url);
    const originalSigned = signedUrlMap.get(originalPath) || imageData.original_url;
    const stagedSigned = stagedData?.staged_url
      ? (signedUrlMap.get(extractFilePath(stagedData.staged_url)) || stagedData.staged_url)
      : null;
    const payloadStyle = typeof payload.styleLabel === 'string'
      ? payload.styleLabel
      : (typeof payload.style === 'string' ? payload.style : null);
    const payloadStructure = typeof payload.structureTypeId === 'string'
      ? payload.structureTypeId
      : (typeof payload.structure_type === 'string' ? payload.structure_type : null);

    return {
      id: item.id,
      status: item.status as StagingJobStatus,
      queued_at: item.queued_at,
      started_at: item.started_at,
      completed_at: item.completed_at,
      attempts: item.attempts,
      last_error: item.last_error,
      image_id: item.image_id,
      project_id: imageData.projects.id,
      project_title: imageData.projects.title,
      project_address: imageData.projects.property_address,
      original_url: imageData.original_url,
      original_signed_url: originalSigned,
      staged_signed_url: stagedSigned,
      staged_url: stagedData?.staged_url || null,
      style_label: stagedData?.style || payloadStyle,
      structure_type: stagedData?.structure_type || payloadStructure,
    };
  });
}

export function useStagingJobsGallery() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.stagingJobs(user?.id),
    queryFn: () => fetchStagingJobs(user?.id || ''),
    enabled: !!user?.id,
    staleTime: 10_000,
    gcTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('staging-jobs-gallery')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'staging_jobs', filter: `user_id=eq.${user.id}` },
        async (payload) => {
          const next = payload.new as {
            id: string;
            status: StagingJobStatus;
            result_staged_image_id?: string | null;
            image_id?: string | null;
          };

          let didUpdate = false;
          queryClient.setQueryData<StagingJobWithContext[]>(
            queryKeys.stagingJobs(user.id),
            (current) => {
              if (!current) return current;
              const idx = current.findIndex((job) => job.id === next.id);
              if (idx === -1) return current;
              const updated = [...current];
              updated[idx] = {
                ...updated[idx],
                status: next.status,
                completed_at: next.status === 'completed' ? new Date().toISOString() : updated[idx].completed_at,
              };
              didUpdate = true;
              return updated;
            }
          );
          if (!didUpdate) {
            queryClient.invalidateQueries({ queryKey: queryKeys.stagingJobs(user.id) });
          }

          if (next.status === 'completed' && next.result_staged_image_id) {
            const { data: staged, error } = await supabase
              .from('staged_images')
              .select('staged_url, style, structure_type')
              .eq('id', next.result_staged_image_id)
              .single();

            if (!error && staged?.staged_url) {
              const signedMap = await getSignedUrlsCached([extractFilePath(staged.staged_url)]);
              const stagedSigned = signedMap.get(extractFilePath(staged.staged_url)) || staged.staged_url;

              queryClient.setQueryData<StagingJobWithContext[]>(
                queryKeys.stagingJobs(user.id),
                (current) => {
                  if (!current) return current;
                  const idx = current.findIndex((job) => job.id === next.id);
                  if (idx === -1) return current;
                  const updated = [...current];
                  updated[idx] = {
                    ...updated[idx],
                    staged_url: staged.staged_url,
                    staged_signed_url: stagedSigned,
                    style_label: staged.style || updated[idx].style_label,
                    structure_type: staged.structure_type || updated[idx].structure_type,
                  };
                  return updated;
                }
              );
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  return {
    jobs: query.data || [],
    loading: query.isLoading,
    error: query.error ? 'Failed to load results' : null,
    count: query.data?.length || 0,
  };
}
